/**
 * Geometry for the Model-shape blocks, as ready-to-emit quads:
 *  - doors: vanilla's 3px panel (DoorBlock.getShape for the id's facing, hinge
 *    and open state — see blocks.ts), textured like models/block/door_*: on
 *    both broad faces the texture's hinge edge (u = 0) sits on the hinge, so
 *    the handle is always on the free edge.
 *  - chest and bed: vanilla block-entity models (ChestRenderer, BedRenderer),
 *    baked by entityModels.ts from the real ModelPart definitions. Facing is a
 *    transform of the whole model, never a per-direction texture.
 *
 * Pure data + logic (no DOM) so the mesh worker and the HUD can import it.
 */

import {
  anvilState, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, chestFacing, DOOR_STATE, doorShape, type DoorState,
  ENCHANTING_TABLE_ID, ENTITY_TILE_BASE, FARMLAND_DRY_ID, FARMLAND_ID, isChestOpen, NETHER_PORTAL_ID, NETHER_PORTAL_Z_ID, NETHER_BRICK_FENCE_ID, NETHER_BRICK_STAIRS_IDS, IS_OPAQUE,
  END_PORTAL_FRAME_ID, END_PORTAL_FRAME_EYE_ID, END_PORTAL_ID, T,
  CHORUS_FLOWER_ID, CHORUS_PLANT_ID, END_GATEWAY_ID, IRON_BARS_ID, WITHER_SKULL_ID,
} from './blocks';
import { bedQuads, chestQuads, orientedQuad, type Quad } from './entityModels';
import { SHAPE_STATE } from './blocks';
import { utilityQuads } from './game/utility/models';
import { stateCollision, stateVisual } from './shapes';

export type { Quad } from './entityModels';
import { redstoneQuads } from './world/redstone/models';

/** Neighbour ids a model's orientation is read from (the bed's other half). */
export interface ModelNb {
  px: number; nx: number; pz: number; nz: number;
  /** Any neighbour by offset (given for redstone dust, whose shape depends on the diagonals). */
  at?(dx: number, dy: number, dz: number): number;
}

/** Unit (x, z) of each facing, chest order: +Z, +X, -Z, -X. */
const FACING_VEC: ReadonlyArray<readonly [number, number]> = [[0, 1], [1, 0], [0, -1], [-1, 0]];

/**
 * A door state's panel. The hinge is the panel's vertical edge at the corner
 * between the side the closed door sits on (opposite its facing) and the hinge
 * side (the placer's left or right); u runs from there (0) to the handle (1).
 */
function doorQuads(st: DoorState, tile: number): Quad[] {
  const r = doorShape(st);
  const min = [r[0] / 16, r[1] / 16, r[2] / 16];
  const max = [r[3] / 16, r[4] / 16, r[5] / 16];
  const [fx, fz] = FACING_VEC[st.facing];
  // placer's right of (fx, fz) is (-fz, fx); left is the opposite
  const hx = st.hinge === 1 ? -fz : fz, hz = st.hinge === 1 ? fx : -fx;
  const hinge = [fx !== 0 ? (fx < 0 ? 1 : 0) : (hx > 0 ? 1 : 0), 0, fz !== 0 ? (fz < 0 ? 1 : 0) : (hz > 0 ? 1 : 0)];
  const long = max[0] - min[0] > max[2] - min[2] ? 0 : 2; // axis the panel spans
  const thin = 2 - long;
  const along = (p: readonly number[]): number => Math.abs(p[long] - hinge[long]);
  const across = (p: readonly number[]): number => p[thin] - min[thin];

  const out: Quad[] = [];
  for (let a = 0; a < 3; a++) {
    const b = (a + 1) % 3, c = (a + 2) % 3;
    for (const side of [0, 1]) {
      const pts = [[0, 0], [1, 0], [1, 1], [0, 1]].map(([i, j]) => {
        const p = [0, 0, 0];
        p[a] = side ? max[a] : min[a];
        p[b] = i ? max[b] : min[b];
        p[c] = j ? max[c] : min[c];
        return p;
      });
      const uvs = pts.map((p): [number, number] =>
        a === thin ? [along(p), 1 - p[1]]     // broad faces: the full texture
          : a === long ? [across(p), 1 - p[1]] // the narrow ends: a 3px strip
          : [along(p), across(p)]);            // top and bottom
      const n = [0, 0, 0];
      n[a] = side ? 1 : -1;
      out.push(orientedQuad(pts, uvs, tile, n));
    }
  }
  return out;
}

/** Bed facing (foot -> head, chest facing order) read off the other half's
 *  position; a lone half (other half missing) points south. */
function bedFacing(head: boolean, nb: ModelNb): number {
  const other = head ? BED_FOOT_ID : BED_HEAD_ID;
  // direction from this half to the other: foot->head is the facing, head->foot its opposite
  const toOther = nb.pz === other ? 0 : nb.px === other ? 1 : nb.nz === other ? 2 : nb.nx === other ? 3 : -1;
  if (toOther < 0) return 0;
  return head ? (toOther + 2) & 3 : toOther;
}

const cache = new Map<string, readonly Quad[]>();
function cached(key: string, make: () => Quad[]): readonly Quad[] {
  let q = cache.get(key);
  if (!q) cache.set(key, (q = make()));
  return q;
}

/**
 * Every quad of a Model-shape block, in block space (0..1, the cell origin at
 * 0). Only the bed reads `nb`; everything else carries its state in its id.
 * Results are cached, so callers must not mutate them.
 */
export function modelQuads(id: number, nb: ModelNb): readonly Quad[] {
  const rs = redstoneQuads(id, nb);
  if (rs) return rs;
  if (id === BED_FOOT_ID || id === BED_HEAD_ID) {
    const head = id === BED_HEAD_ID;
    const f = bedFacing(head, nb);
    return cached(`bed${+head}:${f}`, () => bedQuads(head, f, ENTITY_TILE_BASE));
  }
  // containers and utility blocks, every chest included (game/utility/models.ts)
  const util = utilityQuads(id);
  if (util) return util;
  const facing = chestFacing(id);
  if (facing >= 0) {
    const open = isChestOpen(id);
    return cached(`chest${+open}:${facing}`, () => chestQuads(facing, open, ENTITY_TILE_BASE));
  }
  // farmland: vanilla 15/16-tall cuboid
  if (id === FARMLAND_ID || id === FARMLAND_DRY_ID) {
    const t = BLOCKS[id].tiles;
    return cached(`farmland${id}`, () => boxQuads([0, 0, 0], [16, 15, 16], t));
  }
  if (id === ENCHANTING_TABLE_ID) {
    const t = BLOCKS[id].tiles;
    return cached('ench', () => boxQuads([0, 0, 0], [16, 12, 16], t));
  }
  const anvil = anvilState(id);
  if (anvil) {
    return cached(`anvil${id}`, () => {
      const t = BLOCKS[id].tiles, body = t[0], top = t[2];
      const swap = (anvil[1] & 1) === 1;
      const parts: [number[], number[], boolean][] = [
        [[2, 0, 2], [14, 4, 14], false], [[4, 4, 3], [12, 5, 13], false],
        [[6, 5, 4], [10, 10, 12], false], [[3, 10, 0], [13, 16, 16], true],
      ];
      const out: Quad[] = [];
      for (const [a0, b0, isTop] of parts) {
        const a1 = swap ? [a0[2], a0[1], a0[0]] : a0, b1 = swap ? [b0[2], b0[1], b0[0]] : b0;
        out.push(...boxQuads(a1, b1, [body, body, isTop ? top : body, body, body, body]));
      }
      return out;
    });
  }
  // slabs, stairs, fences, gates, walls, panes, trapdoors: the state is in the id (shapes.ts)
  const shape = SHAPE_STATE[id];
  if (shape) {
    return cached(`shape${id}`, () => {
      const t = BLOCKS[id].tiles, out: Quad[] = [];
      for (const b of stateVisual(shape)) out.push(...boxQuads(b.slice(0, 3), b.slice(3), t));
      return out;
    });
  }
  const door = DOOR_STATE[id];
  if (door) return cached(`door${id}`, () => doorQuads(door, BLOCKS[id].tiles[0]));
  if (id === NETHER_BRICK_FENCE_ID) {
    const t = BLOCKS[id].tiles;
    const joins = (n: number): boolean => n === NETHER_BRICK_FENCE_ID || (IS_OPAQUE[n] === 1);
    const m = (joins(nb.px) ? 1 : 0) | (joins(nb.nx) ? 2 : 0) | (joins(nb.pz) ? 4 : 0) | (joins(nb.nz) ? 8 : 0);
    return cached(`nbfence${m}`, () => {
      const q = boxQuads([6, 0, 6], [10, 16, 10], t);
      // vanilla fence arms: two 2px bars at y 6..9 and 12..15
      for (const [y0, y1] of [[6, 9], [12, 15]]) {
        if (m & 1) q.push(...boxQuads([10, y0, 7], [16, y1, 9], t));
        if (m & 2) q.push(...boxQuads([0, y0, 7], [6, y1, 9], t));
        if (m & 4) q.push(...boxQuads([7, y0, 10], [9, y1, 16], t));
        if (m & 8) q.push(...boxQuads([7, y0, 0], [9, y1, 6], t));
      }
      return q;
    });
  }
  const stair = NETHER_BRICK_STAIRS_IDS.indexOf(id);
  if (stair >= 0) {
    const t = BLOCKS[id].tiles;
    const step: number[][] = [[[0, 8, 8], [16, 16, 16]], [[8, 8, 0], [16, 16, 16]], [[0, 8, 0], [16, 16, 8]], [[0, 8, 0], [8, 16, 16]]][stair];
    return cached(`nbstair${stair}`, () => [...boxQuads([0, 0, 0], [16, 8, 16], t), ...boxQuads(step[0], step[1], t)]);
  }
  // nether portal: vanilla 6/16-thick pane centred in the cell, along its frame's axis
  if (id === NETHER_PORTAL_ID || id === NETHER_PORTAL_Z_ID) {
    const t = BLOCKS[id].tiles;
    return cached(`portal${id}`, () => (id === NETHER_PORTAL_ID
      ? boxQuads([0, 0, 5], [16, 16, 11], t) : boxQuads([5, 0, 0], [11, 16, 16], t)));
  }
  // end portal frame: 13/16 slab; with an eye, vanilla's 8x3x8 eye cube sits on top
  if (id === END_PORTAL_FRAME_ID || id === END_PORTAL_FRAME_EYE_ID) {
    const t = BLOCKS[id].tiles;
    return cached(`endframe${id}`, () => {
      const q = boxQuads([0, 0, 0], [16, 13, 16], t);
      if (id === END_PORTAL_FRAME_EYE_ID) q.push(...boxQuads([4, 13, 4], [12, 16, 12], Array<number>(6).fill(T.END_FRAME_EYE)));
      return q;
    });
  }
  // iron bars: a 2px post, plus a 2px-thick arm toward each bars / opaque neighbour (a lone bar is a plus)
  if (id === IRON_BARS_ID) {
    const t = BLOCKS[id].tiles;
    const joins = (n: number): boolean => n === IRON_BARS_ID || IS_OPAQUE[n] === 1;
    const m = (joins(nb.px) ? 1 : 0) | (joins(nb.nx) ? 2 : 0) | (joins(nb.pz) ? 4 : 0) | (joins(nb.nz) ? 8 : 0);
    return cached(`bars${m}`, () => {
      const q = boxQuads([7, 0, 7], [9, 16, 9], t);
      const any = m !== 0;
      if (m & 1 || !any) q.push(...boxQuads([9, 0, 7], [16, 16, 9], t));
      if (m & 2 || !any) q.push(...boxQuads([0, 0, 7], [7, 16, 9], t));
      if (m & 4 || !any) q.push(...boxQuads([7, 0, 9], [9, 16, 16], t));
      if (m & 8 || !any) q.push(...boxQuads([7, 0, 0], [9, 16, 7], t));
      return q;
    });
  }
  // chorus plant: a thick stem with arms toward neighbouring chorus / end stone; flower: a squat bloom
  if (id === CHORUS_PLANT_ID) {
    const t = BLOCKS[id].tiles;
    const joins = (n: number): boolean => n === CHORUS_PLANT_ID || n === CHORUS_FLOWER_ID;
    const m = (joins(nb.px) ? 1 : 0) | (joins(nb.nx) ? 2 : 0) | (joins(nb.pz) ? 4 : 0) | (joins(nb.nz) ? 8 : 0);
    return cached(`chorus${m}`, () => {
      const q = boxQuads([4, 0, 4], [12, 16, 12], t);
      if (m & 1) q.push(...boxQuads([12, 4, 4], [16, 12, 12], t));
      if (m & 2) q.push(...boxQuads([0, 4, 4], [4, 12, 12], t));
      if (m & 4) q.push(...boxQuads([4, 4, 12], [12, 12, 16], t));
      if (m & 8) q.push(...boxQuads([4, 4, 0], [12, 12, 4], t));
      return q;
    });
  }
  if (id === CHORUS_FLOWER_ID) return cached('chorusflower', () => boxQuads([2, 0, 2], [14, 14, 14], BLOCKS[id].tiles));
  if (id === END_GATEWAY_ID) return cached('endgateway', () => boxQuads([0, 0, 0], [16, 16, 16], BLOCKS[id].tiles));
  // wither skeleton skull: the 8x8x8 head on the floor
  if (id === WITHER_SKULL_ID) return cached('witherskull', () => boxQuads([4, 0, 4], [12, 8, 12], BLOCKS[id].tiles));
  // end portal: vanilla 12/16-tall pane
  if (id === END_PORTAL_ID) return cached('endportal', () => boxQuads([0, 0, 0], [16, 12, 16], BLOCKS[id].tiles));
  return [];
}

/**
 * Axis-aligned cuboid in 1/16 units with vanilla's default per-face UVs (taken
 * from the element's position, so a 12-tall side shows the texture's lower 12 px).
 * tiles: [+X, -X, +Y, -Y, +Z, -Z].
 */
export function boxQuads(min: readonly number[], max: readonly number[], tiles: readonly number[]): Quad[] {
  const lo = min.map((v) => v / 16), hi = max.map((v) => v / 16);
  const out: Quad[] = [];
  for (let a = 0; a < 3; a++) {
    const b = (a + 1) % 3, c = (a + 2) % 3;
    for (const side of [1, 0]) {
      const pts = [[0, 0], [1, 0], [1, 1], [0, 1]].map(([i, j]) => {
        const p = [0, 0, 0];
        p[a] = side ? hi[a] : lo[a];
        p[b] = i ? hi[b] : lo[b];
        p[c] = j ? hi[c] : lo[c];
        return p;
      });
      const uvs = pts.map((p): [number, number] =>
        a === 1 ? [p[0], p[2]] : a === 0 ? [side ? 1 - p[2] : p[2], 1 - p[1]] : [side ? p[0] : 1 - p[0], 1 - p[1]]);
      const n = [0, 0, 0];
      n[a] = side ? 1 : -1;
      out.push(orientedQuad(pts, uvs, tiles[a * 2 + (side ? 0 : 1)], n));
    }
  }
  return out;
}

/**
 * What a slab / stair id fills of each cell face, per direction (+X, -X, +Y, -Y, +Z, -Z): rectangles
 * [u0, v0, u1, v1] in 1/16 on the face's two other axes (ascending axis order). The mesher drops a
 * quad lying on a cell boundary when the neighbour covers it from the other side.
 */
export function shapeCover(id: number): ReadonlyArray<ReadonlyArray<readonly number[]>> | undefined {
  const st = SHAPE_STATE[id];
  if (!st) return undefined;
  let c = coverCache.get(id);
  if (!c) {
    c = [[], [], [], [], [], []];
    for (const b of stateCollision(st)) {
      for (let a = 0; a < 3; a++) {
        const u = (a + 1) % 3, v = (a + 2) % 3;
        const rect = [b[u], b[v], b[u + 3], b[v + 3]];
        if (b[a + 3] === 16) c[a * 2].push(rect);
        if (b[a] === 0) c[a * 2 + 1].push(rect);
      }
    }
    coverCache.set(id, c);
  }
  return c;
}
const coverCache = new Map<number, number[][][]>();
